import { existsSync } from 'node:fs';
import { mkdir, rm, writeFile } from 'node:fs/promises';
import { basename, dirname, join, relative } from 'node:path';
import { parseSkillFile } from './loader.js';
import type { SkillMetadata } from './types.js';
import { clearSkillCache, discoverSkills } from './registry.js';
import { maxPath } from '../utils/paths.js';

interface GitHubRepo {
  default_branch?: string;
  message?: string;
}

interface GitTreeEntry {
  path?: string;
  type?: string;
}

interface GitTree {
  tree?: GitTreeEntry[];
  message?: string;
}

export interface ImportSkillsOptions {
  packageRef: string;
  skills?: string[];
  scope?: 'user' | 'project';
  force?: boolean;
}

export interface ImportedSkill {
  name: string;
  description: string;
  path: string;
  files: number;
}

export interface ImportSkillsResult {
  destinationRoot: string;
  imported: ImportedSkill[];
  skipped: string[];
}

function skillsRoot(scope: 'user' | 'project' = 'user'): string {
  return scope === 'project' ? join(process.cwd(), '.max', 'skills') : maxPath('skills');
}

function parsePackageRef(packageRef: string): { owner: string; repo: string } {
  const trimmed = packageRef.trim().replace(/^https:\/\/github\.com\//, '').replace(/\.git$/, '');
  const [owner, repo, extra] = trimmed.split('/');
  if (!owner || !repo || extra) {
    throw new Error('Expected a skills.sh package like "owner/repo" or "https://github.com/owner/repo".');
  }
  return { owner, repo };
}

function normalizeSkillFilter(skills?: string[]): Set<string> | null {
  const cleaned = skills?.map((skill) => skill.trim()).filter(Boolean) ?? [];
  return cleaned.length > 0 ? new Set(cleaned) : null;
}

function safeSkillDirName(name: string, fallback: string): string {
  const normalized = name.toLowerCase().replace(/[^a-z0-9._-]+/g, '-').replace(/^-+|-+$/g, '');
  return normalized || fallback;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    headers: {
      Accept: 'application/vnd.github+json',
      'User-Agent': '@manasija/max skills importer',
    },
  });
  const text = await response.text();
  if (!response.ok) {
    let message = text;
    try {
      message = (JSON.parse(text) as { message?: string }).message ?? text;
    } catch {
      // keep the raw response
    }
    throw new Error(`GitHub request failed (${response.status}): ${message}`);
  }
  return JSON.parse(text) as T;
}

async function fetchBytes(url: string): Promise<Uint8Array> {
  const response = await fetch(url, { headers: { 'User-Agent': '@manasija/max skills importer' } });
  if (!response.ok) {
    throw new Error(`Failed to download ${url} (${response.status})`);
  }
  return new Uint8Array(await response.arrayBuffer());
}

function rawUrl(owner: string, repo: string, branch: string, path: string): string {
  return `https://raw.githubusercontent.com/${owner}/${repo}/${encodeURIComponent(branch)}/${path.split('/').map(encodeURIComponent).join('/')}`;
}

function isInside(parent: string, child: string): boolean {
  const rel = relative(parent, child);
  return rel === '' || (!!rel && !rel.startsWith('..') && !rel.startsWith('/'));
}

async function discoverRemoteSkills(owner: string, repo: string) {
  const repoInfo = await fetchJson<GitHubRepo>(`https://api.github.com/repos/${owner}/${repo}`);
  const branch = repoInfo.default_branch;
  if (!branch) {
    throw new Error(repoInfo.message ?? `Could not resolve default branch for ${owner}/${repo}.`);
  }

  const gitTree = await fetchJson<GitTree>(
    `https://api.github.com/repos/${owner}/${repo}/git/trees/${encodeURIComponent(branch)}?recursive=1`,
  );
  const files = (gitTree.tree ?? []).filter((entry) => entry.type === 'blob' && entry.path);
  const skillFiles = files
    .map((entry) => entry.path as string)
    .filter((path) => basename(path).toLowerCase() === 'skill.md');

  if (skillFiles.length === 0) {
    throw new Error(`No SKILL.md files found in ${owner}/${repo}.`);
  }

  return { branch, files: files.map((entry) => entry.path as string), skillFiles };
}

export async function importSkillsFromPackage(options: ImportSkillsOptions): Promise<ImportSkillsResult> {
  const { owner, repo } = parsePackageRef(options.packageRef);
  const selectedSkills = normalizeSkillFilter(options.skills);
  const destinationRoot = skillsRoot(options.scope);
  const remote = await discoverRemoteSkills(owner, repo);
  const imported: ImportedSkill[] = [];
  const skipped: string[] = [];
  const matchedSelections = new Set<string>();
  const candidateSkillFiles = selectedSkills
    ? remote.skillFiles.filter((skillFile) => {
        const skillDir = dirname(skillFile) === '.' ? '' : dirname(skillFile);
        const folderName = basename(skillDir || repo);
        return selectedSkills.has(folderName) || selectedSkills.has(skillDir) || selectedSkills.has(skillFile);
      })
    : remote.skillFiles;
  const skillFilesToInspect = candidateSkillFiles.length > 0 ? candidateSkillFiles : remote.skillFiles;

  await mkdir(destinationRoot, { recursive: true });

  for (const skillFile of skillFilesToInspect) {
    const skillDir = dirname(skillFile) === '.' ? '' : dirname(skillFile);
    const skillMarkdown = new TextDecoder().decode(
      await fetchBytes(rawUrl(owner, repo, remote.branch, skillFile)),
    );
    const parsed = parseSkillFile(skillMarkdown, skillFile, 'user');
    const folderName = basename(skillDir || repo);
    const matches =
      !selectedSkills ||
      selectedSkills.has(parsed.name) ||
      selectedSkills.has(folderName) ||
      selectedSkills.has(skillDir) ||
      selectedSkills.has(skillFile);

    if (!matches) {
      if (!selectedSkills) skipped.push(parsed.name);
      continue;
    }
    if (selectedSkills) {
      for (const selected of selectedSkills) {
        if (
          selected === parsed.name ||
          selected === folderName ||
          selected === skillDir ||
          selected === skillFile
        ) {
          matchedSelections.add(selected);
        }
      }
    }

    const dirName = safeSkillDirName(parsed.name, folderName);
    const destination = join(destinationRoot, dirName);
    if (!isInside(destinationRoot, destination)) {
      throw new Error(`Refusing unsafe destination for skill "${parsed.name}".`);
    }
    if (existsSync(destination)) {
      if (!options.force) {
        skipped.push(`${parsed.name} (already installed)`);
        continue;
      }
      await rm(destination, { recursive: true, force: true });
    }

    const filesToCopy = remote.files.filter((path) => {
      if (!skillDir) return !path.includes('/');
      return path === skillDir || path.startsWith(`${skillDir}/`);
    });

    let copied = 0;
    for (const filePath of filesToCopy) {
      const relativePath = skillDir ? relative(skillDir, filePath) : filePath;
      if (relativePath.startsWith('..') || relativePath.startsWith('/')) continue;
      const outputPath = join(destination, relativePath);
      if (!isInside(destination, outputPath)) continue;
      await mkdir(dirname(outputPath), { recursive: true });
      await writeFile(outputPath, await fetchBytes(rawUrl(owner, repo, remote.branch, filePath)));
      copied += 1;
    }

    imported.push({
      name: parsed.name,
      description: parsed.description,
      path: join(destination, 'SKILL.md'),
      files: copied,
    });
  }

  if (selectedSkills) {
    const found = new Set([
      ...matchedSelections,
      ...imported.map((skill) => skill.name),
      ...skipped.map((name) => name.replace(/ \(already installed\)$/, '')),
    ]);
    const missing = [...selectedSkills].filter((skill) => !found.has(skill));
    if (missing.length > 0) {
      skipped.push(...missing.map((skill) => `${skill} (not found)`));
    }
  }

  clearSkillCache();
  return { destinationRoot, imported, skipped };
}

export function listInstalledSkills(): SkillMetadata[] {
  clearSkillCache();
  return discoverSkills();
}

export async function deleteInstalledSkill(skill: SkillMetadata): Promise<string> {
  if (skill.source === 'builtin') {
    throw new Error(`Cannot delete built-in skill "${skill.name}".`);
  }

  const root = skill.source === 'project' ? join(process.cwd(), '.max', 'skills') : maxPath('skills');
  const skillDir = dirname(skill.path);
  if (!isInside(root, skillDir)) {
    throw new Error(`Refusing to delete skill outside ${root}.`);
  }

  await rm(skillDir, { recursive: true, force: true });
  clearSkillCache();
  return skillDir;
}
